import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../../services/api';

interface WinRateGraphModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
}

interface WinRateData {
  date: string;
  winning: number;
  losing: number;
  winRate: number;
}

const WinRateGraphModal: React.FC<WinRateGraphModalProps> = ({ isOpen, onClose, isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<WinRateData[]>([]);
  const [period, setPeriod] = useState<'week' | 'month' | '3month' | 'year'>('month');
  const [totalStats, setTotalStats] = useState({ winning: 0, losing: 0, winRate: 0 });
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchWinRateData();
    }
  }, [isOpen, period, isAdmin]);

  const fetchWinRateData = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const now = new Date();
      let startDate = new Date();
      
      switch (period) {
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case '3month':
          startDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      // Fetch trades for the period
      // IMPORTANT: Regular users use /trades which filters by userId automatically
      // Admin users use /admin/all-trades which gets all trades from all active accounts
      const params = {
        startDate: startDate.toISOString(),
        limit: 5000
      };
      
      console.log('[WinRateModal] Fetching trades:', { isAdmin, params, period });
      
      let response;
      try {
        if (isAdmin) {
          response = await api.get('/admin/all-trades', { params });
        } else {
          response = await api.get('/trades', { params });
        }
      } catch (apiError: any) {
        console.error('[WinRateModal] API error:', apiError?.response?.data || apiError);
        throw apiError;
      }
      
      // Handle multiple possible response structures
      let trades: any[] = [];
      if (response.data?.data?.trades) {
        trades = response.data.data.trades;
      } else if (response.data?.trades) {
        trades = response.data.trades;
      } else if (Array.isArray(response.data?.data)) {
        trades = response.data.data;
      } else if (Array.isArray(response.data)) {
        trades = response.data;
      }

      console.log('[WinRateModal] Fetched trades:', {
        isAdmin,
        totalTrades: trades.length,
        responseStructure: Object.keys(response.data || {}),
        sampleTrade: trades[0]
      });

      // Group trades by date and calculate win/loss
      const tradesByDate: { [date: string]: { winning: number; losing: number } } = {};
      let totalWinning = 0;
      let totalLosing = 0;
      
      trades.forEach((trade: any) => {
        // Use filledAt if available, otherwise use createdAt as fallback
        const dateField = trade.filledAt || trade.createdAt;
        
        // Only count trades with realizedPnl (win/loss can only be determined from P&L)
        if (trade.status === 'FILLED' && dateField && trade.realizedPnl !== null && trade.realizedPnl !== undefined) {
          const date = new Date(dateField).toISOString().split('T')[0];
          if (date) {
            const pnl = Number(trade.realizedPnl);
            
            if (!tradesByDate[date]) {
              tradesByDate[date] = { winning: 0, losing: 0 };
            }
            
            if (pnl > 0) {
              tradesByDate[date]!.winning++;
              totalWinning++;
            } else if (pnl < 0) {
              tradesByDate[date]!.losing++;
              totalLosing++;
            }
            // Note: pnl === 0 (break-even) trades are excluded from win/loss count
          }
        }
      });

      console.log('[WinRateModal] Processed win rate data:', {
        datesWithData: Object.keys(tradesByDate).length,
        totalWinning,
        totalLosing,
        winRate: totalWinning + totalLosing > 0 ? ((totalWinning / (totalWinning + totalLosing)) * 100).toFixed(1) + '%' : '0%'
      });

      // Convert to array and calculate win rate per day
      const sortedDates = Object.keys(tradesByDate).sort();
      const chartData: WinRateData[] = sortedDates.map(date => {
        const dayData = tradesByDate[date];
        if (!dayData) {
          return {
            date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            winning: 0,
            losing: 0,
            winRate: 0
          };
        }
        const { winning, losing } = dayData;
        const total = winning + losing;
        const winRate = total > 0 ? (winning / total) * 100 : 0;
        return {
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          winning,
          losing,
          winRate: Number(winRate.toFixed(1))
        };
      });

      const totalTrades = totalWinning + totalLosing;
      const overallWinRate = totalTrades > 0 ? (totalWinning / totalTrades) * 100 : 0;

      setData(chartData);
      setTotalStats({
        winning: totalWinning,
        losing: totalLosing,
        winRate: Number(overallWinRate.toFixed(1))
      });
    } catch (error: any) {
      console.error('Failed to fetch win rate data:', error);
      setData([]);
      setTotalStats({ winning: 0, losing: 0, winRate: 0 });
      const msg = error?.response?.data?.error?.message 
        || error?.response?.data?.error 
        || (typeof error?.message === 'string' ? error.message : null);
      setFetchError(msg || 'Failed to load win rate data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const pieData = [
    { name: 'Winning Trades', value: totalStats.winning, color: '#10B981' },
    { name: 'Losing Trades', value: totalStats.losing, color: '#EF4444' }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 rounded-xl border border-neutral-700 max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-700 sticky top-0 bg-neutral-900 z-10">
          <div>
            <h2 className="text-2xl font-bold text-white">Win Rate Analysis</h2>
            <p className="text-sm text-neutral-400 mt-1">Track your trading success rate over time</p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white transition-colors p-2 hover:bg-neutral-800 rounded-lg"
            aria-label="Close modal"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Period Selector */}
        <div className="px-6 py-4 border-b border-neutral-800">
          <div className="flex gap-2">
            {[
              { value: 'week', label: '1 Week' },
              { value: 'month', label: '1 Month' },
              { value: '3month', label: '3 Months' },
              { value: 'year', label: '1 Year' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setPeriod(value as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  period === value
                    ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-600/30'
                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
            </div>
          ) : fetchError ? (
            <div className="h-96 flex flex-col items-center justify-center text-neutral-400">
              <svg className="h-16 w-16 mb-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-lg font-medium text-white">Unable to load win rate data</p>
              <p className="text-sm mt-1 max-w-sm text-center">{fetchError}</p>
              <button
                onClick={fetchWinRateData}
                className="mt-4 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-500 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : data.length > 0 ? (
            <div className="space-y-8">
              {/* Overall Stats with Pie Chart */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-neutral-800/50 rounded-lg p-6 border border-neutral-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Overall Performance</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-neutral-400">Win Rate</p>
                      <p className="text-3xl font-bold text-cyan-400">{totalStats.winRate}%</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-neutral-400">Winning Trades</p>
                        <p className="text-2xl font-bold text-green-400">{totalStats.winning}</p>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-400">Losing Trades</p>
                        <p className="text-2xl font-bold text-red-400">{totalStats.losing}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-400">Total Trades</p>
                      <p className="text-xl font-semibold text-white">{totalStats.winning + totalStats.losing}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-800/50 rounded-lg p-6 border border-neutral-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Win/Loss Distribution</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1F2937', 
                          border: '1px solid #374151',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Daily Win/Loss Chart */}
              <div className="bg-neutral-800/50 rounded-lg p-6 border border-neutral-700">
                <h3 className="text-lg font-semibold text-white mb-4">Daily Win Rate</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9CA3AF"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      stroke="#9CA3AF"
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1F2937', 
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F3F4F6'
                      }}
                      formatter={(value: any, name: string) => {
                        if (name === 'winRate') return [`${value}%`, 'Win Rate'];
                        return [value, name === 'winning' ? 'Winning' : 'Losing'];
                      }}
                      labelStyle={{ color: '#9CA3AF' }}
                    />
                    <Legend wrapperStyle={{ color: '#9CA3AF' }} />
                    <Bar dataKey="winning" name="Winning" fill="#10B981" stackId="a" />
                    <Bar dataKey="losing" name="Losing" fill="#EF4444" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="h-96 flex flex-col items-center justify-center text-neutral-400">
              <svg className="h-16 w-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-lg font-medium">No win rate data available</p>
              <p className="text-sm mt-1">Start trading to see your win/loss analysis</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WinRateGraphModal;
